import { DIRECTED_CAST_PENDING_REASON } from 'fym-shared/src/music24/directedCastRules';
import { MUSIC_VIDEO_ERROR_CODES } from 'fym-shared/src/music24/musicVideoApi';
import { coverErrorCode } from '../coverEditor/doc/coverEditorErrors';

/** The `M24_*` code of an API error (`cause.code`), or null. Pure. */
export const directedErrorCode = coverErrorCode;

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
}

/** The `cause.reason` of an API error that is not an `M24_*` code ("video", "day", "pictures"), or null. Pure. */
export function directedErrorReason(error: unknown): string | null {
  const reason = asRecord(asRecord(error)?.m24Limit)?.reason;
  return typeof reason === 'string' && !reason.startsWith('M24_') ? reason : null;
}

/** What a refused write of the wizard means for the screen (spec §10, design b3, c4). */
export type DirectedActionError =
  /** 400 `M24_DIRECTED_CONSENT`: a person picture needs the consent tick. */
  | 'consent'
  /** 429 `M24_DIRECTED_LIMIT` reason `pictures`: the 12 AI pictures of the video are used. */
  | 'pictures'
  /** 429 `M24_DIRECTED_LIMIT` reason `cast`: the cast is full. */
  | 'castFull'
  /** 429 `M24_DIRECTED_LIMIT` reason `video`: the director runs of the video are used. */
  | 'videoLimit'
  /** 429 `M24_DIRECTED_LIMIT` reason `day`: the director runs of today are used. */
  | 'dayLimit'
  /** 409 `M24_DIRECTED_BUSY`: the director works, or another write holds the video. */
  | 'busy'
  /** 409 `M24_DIRECTED_STALE`: the video changed on another screen. */
  | 'stale'
  /** 400 `M24_DIRECTED_STEP` reason `cast_pending`: a cast picture is made or read now (owner fix 2026-10-19). */
  | 'castPending'
  /** 400 `M24_DIRECTED_STEP`: the step order or the status does not allow it. */
  | 'step'
  /** 403 `M24_DEMO_READ_ONLY`: the example artist. */
  | 'demo'
  | 'failed';

/** Maps the refusal of a write of the directed video. Pure. */
export function directedActionError(error: unknown): DirectedActionError {
  const code = directedErrorCode(error);
  switch (code) {
    case MUSIC_VIDEO_ERROR_CODES.directedConsent:
      return 'consent';
    case MUSIC_VIDEO_ERROR_CODES.directedLimit: {
      const reason = directedErrorReason(error);
      if (reason === 'pictures') {
        return 'pictures';
      }
      if (reason === 'cast') {
        return 'castFull';
      }
      return reason === 'day' ? 'dayLimit' : 'videoLimit';
    }
    case MUSIC_VIDEO_ERROR_CODES.directedBusy:
      return 'busy';
    case MUSIC_VIDEO_ERROR_CODES.directedStale:
      return 'stale';
    case MUSIC_VIDEO_ERROR_CODES.directedStep:
      return directedErrorReason(error) === DIRECTED_CAST_PENDING_REASON ? 'castPending' : 'step';
    case 'M24_DEMO_READ_ONLY':
      return 'demo';
    case null:
    default:
      return 'failed';
  }
}
